import {
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
  httpLink,
  HTTPLinkOptions,
  HTTPBatchLinkOptions,
} from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import { type inferReactQueryProcedureOptions } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@zapier/bff";
import {
  BFF_API_ROUTE,
  BFF_STREAM_API_ROUTE,
  CSRF_HEADER_NAME,
} from "@zapier/utils";
import { sessionJwtIsAvailable } from "@zapier/identity";

export const getBaseUrl = () => {
  if (globalThis.window !== undefined) return "";
  if (process.env.NEXT_PUBLIC_ZAPIER_ORIGIN)
    return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  return process.env.BASE_URL;
};

// infer the types for AppRouter
export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>;
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

const defaultLinkOptions: HTTPLinkOptions<any> & HTTPBatchLinkOptions<any> = {
  /**
   * If you want to use SSR, you need to use the server's full URL
   * @link https://trpc.io/docs/ssr
   **/
  url: `${getBaseUrl()}${BFF_API_ROUTE}`,
  async fetch(url, options) {
    // Hold every request until the session.jwt cookie is minted so nothing fires
    // unauthenticated on first load. Instant on the server; fails open after 15s.
    await sessionJwtIsAvailable();
    const headers = new Headers(options?.headers);
    headers.set(CSRF_HEADER_NAME, "1");
    return fetch(url, {
      ...options,
      headers,
      credentials: "include",
    });
  },
};

// use httpBatchLink and group multiple requests together
const defaultBatchLink = httpBatchLink({
  ...defaultLinkOptions,
  // max URL length to avoid HTTP 413/414 error
  maxURLLength: 2083,
});

// Conditional link for allowing individual procedures to force use of
// POST requests to avoid URL length limits
const conditionalPostRequestLink = splitLink({
  condition(op) {
    // splitLink allows us to conditionally use a different link based on the operation.
    return Boolean(op?.context?.forcePost);
  },
  // if context.forcePost is true, use httpLink as a POST request and skip batching
  true: httpLink({
    ...defaultLinkOptions,
    // force POST requests
    methodOverride: "POST",
  }),
  false: defaultBatchLink,
});

// Conditional link for skipping batching to tune performance on certain critical procedures
const conditionalSkipBatchingLink = splitLink({
  condition(op) {
    // splitLink allows us to conditionally use a different link based on the operation.
    return Boolean(op?.context?.skipBatching);
  },
  // if context.skipBatching is true, use httpLink and skip batching
  true: httpLink({
    ...defaultLinkOptions,
  }),
  false: conditionalPostRequestLink,
});

const conditionalGenerationStreamLink = splitLink({
  condition: (op) =>
    op.type === "mutation" && op.path === "sdkCredentials.generateRulebook",
  true: httpBatchStreamLink({
    ...defaultLinkOptions,
    url: `${getBaseUrl()}${BFF_STREAM_API_ROUTE}`,
    maxItems: 1,
  }),
  false: conditionalSkipBatchingLink,
});

export const trpc = createTRPCNext<AppRouter>({
  config() {
    return {
      links: [conditionalGenerationStreamLink],
      queryClientConfig: {
        defaultOptions: {
          queries: {
            // default to 20s to avoid excessive refetches
            staleTime: 1000 * 20,
          },
        },
      },
    };
  },
  /**
   * @link https://trpc.io/docs/ssr
   **/
  ssr: false,
});
